import { useCallback, useEffect, useState } from "react";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { useSearchParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "./layoutChrome";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Info, ShieldCheck, HardDriveDownload, Radar } from "lucide-react";
import SatSecureDownloadPanel from "@/components/SatSecureDownloadPanel";
import SatCfdiExportPanel from "@/components/SatCfdiExportPanel";
import SurgicalQueueStatusBanner from "@/components/SurgicalQueueStatusBanner";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { resolveDatosMaestrosAwareBreadcrumb } from "@/modules/datosMaestros/datosMaestrosNavigation";
import { useDatosMaestrosNavigationContext } from "@/modules/datosMaestros/useDatosMaestrosNavigationContext";

/**
 * Fase 2: la pestaña CFDI legacy redirige a Gestionar XML.
 * Desactivar para rollback de emergencia (ver emergency_rollback.md en la raíz del repo).
 */
export const CFDI_TAB_REDIRECT_ENABLED = true;

/**
 * Hub de descarga manual SAT (rescate e.Firma). Sync automática: monitor dedicado.
 */
export default function DescargaAutomaticaSat() {
  const navigate = useNavigate();
  const location = useLocation();
  useDatosMaestrosNavigationContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openSections, setOpenSections] = useState([]);

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [surgicalQueue, setSurgicalQueue] = useState(null);
  const showSyncDiagnostic = usePlatformDesigner();

  const cli = clients.find((c) => c.id === clientId);

  const loadSurgicalQueue = useCallback(async () => {
    if (!clientId || !showSyncDiagnostic) {
      setSurgicalQueue(null);
      return;
    }
    try {
      const { data } = await api.get("/sat/admin/storage-summary", {
        params: { client_id: clientId },
      });
      setSurgicalQueue(data?.surgical_queue || null);
    } catch {
      setSurgicalQueue(null);
    }
  }, [clientId, showSyncDiagnostic]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
  }, []);

  useEffect(() => {
    if (!showSyncDiagnostic) {
      setOpenSections((prev) => prev.filter((s) => s !== "sync"));
    }
  }, [showSyncDiagnostic]);

  useEffect(() => {
    if (!showSyncDiagnostic) {
      setSurgicalQueue(null);
      return undefined;
    }
    loadSurgicalQueue();
    if (!clientId) return undefined;
    const id = window.setInterval(loadSurgicalQueue, 30000);
    return () => window.clearInterval(id);
  }, [clientId, loadSurgicalQueue, showSyncDiagnostic]);

  useEffect(() => {
    const raw = (searchParams.get("tab") || searchParams.get("open") || "").toLowerCase();
    const section = raw === "xml" ? "cfdi" : raw;
    if (CFDI_TAB_REDIRECT_ENABLED && section === "cfdi") {
      navigate("/visores-sat?open=ingresos", { replace: true });
      return;
    }
    if (section === "documentos") {
      navigate("/documentos-fiscales-sat", { replace: true });
      return;
    }
    if (section === "sync") {
      if (!showSyncDiagnostic) {
        navigate("/monitor-sincronizacion-sat", { replace: true });
        return;
      }
      setOpenSections((prev) => (prev.includes("sync") ? prev : [...prev, "sync"]));
      return;
    }
    if (section === "ciec") {
      setOpenSections((prev) => (prev.includes("fiel") ? prev : [...prev, "fiel"]));
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "fiel");
        return next;
      }, { replace: true });
      return;
    }
    if (section === "fiel" || section === "cfdi") {
      setOpenSections((prev) => (prev.includes(section) ? prev : [...prev, section]));
    }
  }, [searchParams, navigate, showSyncDiagnostic]);

  const onOpenSectionsChange = (values) => {
    const allowed = showSyncDiagnostic ? values : values.filter((v) => v !== "sync");
    setOpenSections(allowed);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (allowed.length > 0) {
        next.set("tab", allowed[allowed.length - 1]);
      } else {
        next.delete("tab");
      }
      return next;
    }, { replace: true });
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Descarga de CFDI SAT"
        subtitle={
          showSyncDiagnostic
            ? "Descargue CFDI del portal SAT con la e.Firma del cliente. Rescate puntual por periodo; la sincronización automática cubre el día a día (Clientes → Sincronización SAT)."
            : "Descargue CFDI del portal SAT con la e.Firma del cliente."
        }
        breadcrumb={resolveDatosMaestrosAwareBreadcrumb(
          location,
          VISOR_MODULE_BREADCRUMB_ROOT,
          "Descarga de CFDI SAT",
        )}
      />

      <div className="p-8 space-y-5 max-w-7xl mx-auto">
        {showSyncDiagnostic ? (
        <div
          className="rounded-sm border border-sky-200 bg-sky-50/90 px-4 py-3 text-sm text-slate-800 leading-relaxed flex gap-3"
          role="note"
          data-testid="descarga-sat-designer-info-banner"
        >
          <Info className="w-5 h-5 shrink-0 text-sky-700 mt-0.5" strokeWidth={1.75} aria-hidden />
          <p className="m-0">
            Esta pantalla consulta los webservices oficiales de descarga masiva del SAT o exporta XML ya cargados a su PC.
            Abra la sección que necesite (estilo acordeón, como los visores XML). Para subir archivos manualmente use{" "}
            <strong>Carga de Información</strong>.
          </p>
        </div>
        ) : null}

        {showSyncDiagnostic ? (
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-[#0F2D5C]/20 bg-[#0F2D5C]/5 px-4 py-3">
            <Radar className="w-5 h-5 text-[#0F2D5C] shrink-0" strokeWidth={1.75} />
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-semibold text-[#0F2D5C] m-0">Monitor de sincronización automática</p>
              <p className="text-xs text-slate-600 m-0 mt-0.5">
                Revise si el worker nocturno terminó bien (semáforo). Active clientes en{" "}
                <Link to="/clientes?tab=sincronizacion" className="underline font-medium">Clientes → Sincronización SAT</Link>.
              </p>
            </div>
            <Button size="sm" asChild data-testid="goto-monitor-sincronizacion">
              <Link to="/monitor-sincronizacion-sat">Abrir monitor</Link>
            </Button>
          </div>
        ) : null}

        {showSyncDiagnostic && clientId && surgicalQueue ? (
          <SurgicalQueueStatusBanner surgicalQueue={surgicalQueue} />
        ) : null}

        <div className="bg-white rounded-md border border-slate-200 p-5 space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="descarga-sat-client-select" className="mt-1 h-auto min-h-[40px] py-2 text-left">
                <SelectValue placeholder="Selecciona un cliente" />
              </SelectTrigger>
              <SelectContent className="max-w-[600px]">
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id} className="py-2">
                    <div className="flex flex-col items-start text-left">
                      <div className="font-bold text-sm whitespace-normal leading-tight">{c.razon_social}</div>
                      <div className="text-[10px] text-slate-500 mt-1 font-mono">
                        {c.rfc} {c.owner_email ? ` · ${c.owner_email}` : ""}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Accordion
          type="multiple"
          value={openSections}
          onValueChange={onOpenSectionsChange}
          className="rounded-sm border border-slate-200 bg-white shadow-sm"
        >
          {showSyncDiagnostic ? (
            <AccordionItem value="sync" className="border-slate-200 px-0">
              <AccordionTrigger data-testid="tab-descarga-sync-auto">
                <span className="flex items-center gap-2">
                  <Radar className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                  Estado sincronización SAT (diagnóstico)
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-5 pt-3 border-t border-slate-100 space-y-3">
                <p className="text-xs text-slate-600 m-0">
                  La bitácora completa está en la pantalla dedicada (misma que en producción).
                </p>
                <Button size="sm" asChild data-testid="descarga-goto-monitor-full">
                  <Link to={`/monitor-sincronizacion-sat${clientId ? `?client=${encodeURIComponent(clientId)}` : ""}`}>
                    Abrir Estado sincronización SAT
                  </Link>
                </Button>
              </AccordionContent>
            </AccordionItem>
          ) : null}

          <AccordionItem value="fiel" className="border-slate-200 px-0">
            <AccordionTrigger data-testid="tab-descarga-fiel">
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                Descarga por firma Electrónica (e.Firma)
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-5 pt-3 border-t border-slate-100">
              <div className="pt-1">
                <SatSecureDownloadPanel clientId={clientId} />
              </div>
            </AccordionContent>
          </AccordionItem>

          {!CFDI_TAB_REDIRECT_ENABLED ? (
            <AccordionItem value="cfdi" className="border-slate-200 px-0 border-b-0">
              <AccordionTrigger data-testid="tab-descarga-cfdi">
                <span className="flex items-center gap-2">
                  <HardDriveDownload className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                  Descargas de XML (Comprobantes fiscales)
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-5 pt-0 border-t border-slate-100">
                <div className="pt-4 space-y-4">
                  <SatCfdiExportPanel
                    clientId={clientId}
                    clientRfc={cli?.rfc}
                  />
                  <p className="text-xs text-slate-600 border-t border-slate-100 pt-3">
                    ¿Usa la estructura <strong>BNT.TaxRepository</strong> en disco? La exportación avanzada con árbol
                    organizado y PDFs faltantes está en{" "}
                    <Link to="/visores-sat?open=ingresos" className="text-[#0F2D5C] underline font-medium">
                      Visor → Descargar periodo
                    </Link>
                    {" "}(recomendado) o{" "}
                    <Link to="/visores-sat?open=ingresos" className="text-[#0F2D5C] underline font-medium">
                      Exportación en visor
                    </Link>
                    .
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          ) : (
            <div className="border-t border-slate-200 px-4 py-4 text-xs text-slate-600 space-y-2">
              <p className="m-0">
                La exportación de XML a disco está en el{" "}
                <Link to="/visores-sat?open=ingresos" className="text-[#0F2D5C] underline font-medium">
                  Visor de XML SAT
                </Link>
                {" "}(menú Descargar periodo). La descarga manual por e.Firma sigue en esta pantalla.
              </p>
              <p className="m-0">
                Constancia CSF y opinión de cumplimiento:{" "}
                <Link to="/documentos-fiscales-sat" className="text-[#0F2D5C] underline font-medium">
                  CSF y opinión SAT
                </Link>
                .
              </p>
            </div>
          )}
        </Accordion>
      </div>
    </div>
  );
}
